/**
 * Online Coaching use cases (client) – list coaches, get coach, request coaching, my programs, log workout, my coach.
 */
import { coachRepo } from '../../data/repositories/CoachRepository';
import { coachingRequestRepo } from '../../data/repositories/CoachingRequestRepository';
import { programRepo } from '../../data/repositories/ProgramRepository';
import { programAssignmentRepo } from '../../data/repositories/ProgramAssignmentRepository';
import { programExerciseRepo } from '../../data/repositories/ProgramExerciseRepository';
import { workoutLogRepo } from '../../data/repositories/WorkoutLogRepository';
import { coachClientRepo } from '../../data/repositories/CoachClientRepository';
import { nutritionPlanRepo } from '../../data/repositories/NutritionPlanRepository';
import { programSessionRepo } from '../../data/repositories/ProgramSessionRepository';

export async function getMyCoachId(clientId) {
  return coachClientRepo.getMyCoachId(clientId);
}

export async function getMyNutritionPlan(clientId) {
  return nutritionPlanRepo.getByClient(clientId);
}

export async function listApprovedCoaches(limitCount = 50, specialization = null) {
  return coachRepo.listApproved(limitCount, specialization);
}

export async function getCoach(coachId) {
  return coachRepo.getById(coachId);
}

export async function requestCoaching(clientId, coachId, message = '') {
  const existing = await coachingRequestRepo.getExisting(clientId, coachId);
  if (existing && existing.status === 'pending') throw new Error('You already have a pending request for this coach.');
  if (existing && existing.status === 'accepted') throw new Error('You are already a client of this coach.');
  return coachingRequestRepo.create({ clientId, coachId, message: message.trim() });
}

/**
 * Returns programs the client has access to: assigned programs (denormalized title/weeks on assignment) + legacy programs.
 */
export async function getMyPrograms(clientId) {
  const [legacy, assignments] = await Promise.all([
    programRepo.listByClient(clientId),
    programAssignmentRepo.listByClient(clientId),
  ]);
  const withStartDate = legacy.map((p) => ({ ...p, startDate: null }));
  const assigned = assignments.map((a) => ({
    id: a.programId,
    title: a.programTitle ?? '',
    weeks: a.programWeeks ?? 0,
    startDate: a.startDate,
  }));
  const byId = new Map();
  withStartDate.forEach((p) => byId.set(p.id, p));
  assigned.forEach((p) => byId.set(p.id, p));
  return Array.from(byId.values()).sort((a, b) => {
    const da = a.startDate || a.createdAt?.toISOString?.() || '';
    const db_ = b.startDate || b.createdAt?.toISOString?.() || '';
    return db_.localeCompare(da);
  });
}

/** Get program for view: from assignment (denormalized) if assigned, else from program doc (legacy). */
export async function getProgramForClient(clientId, programId) {
  const assignment = await programAssignmentRepo.getByClientAndProgram(clientId, programId);
  if (assignment) {
    return {
      id: programId,
      title: assignment.programTitle ?? '',
      description: assignment.programDescription ?? '',
      weeks: assignment.programWeeks ?? 0,
      status: 'active',
    };
  }
  return programRepo.getById(programId);
}

export async function getProgram(programId) {
  return programRepo.getById(programId);
}

export async function getProgramExercises(programId) {
  return programExerciseRepo.listByProgram(programId);
}

export async function getWorkoutLogs(clientId, programId) {
  return workoutLogRepo.listByClientAndProgram(clientId, programId);
}

export async function logWorkout(data) {
  return workoutLogRepo.create(data);
}

export async function updateWorkoutLog(logId, data) {
  return workoutLogRepo.update(logId, data);
}

export async function getMyRequests(clientId) {
  return coachingRequestRepo.listByClient(clientId);
}

export async function cancelCoachingRequest(requestId) {
  return coachingRequestRepo.cancel(requestId);
}

export async function finishProgramDay({ clientId, coachId, programId, programTitle, weekNumber, dayNumber, exerciseCount }) {
  return programSessionRepo.create({
    clientId,
    coachId,
    programId,
    programTitle: programTitle || '',
    weekNumber: Number(weekNumber) || 1,
    dayNumber: Number(dayNumber) || 1,
    exerciseCount: Number(exerciseCount) || 0,
  });
}

export async function getMyProgramSessions(clientId, programId) {
  return programSessionRepo.listByClientProgram(clientId, programId);
}

export async function getActiveCoachRelationship(clientId) {
  return coachClientRepo.getActiveByClient(clientId);
}

export async function endCoaching(relId) {
  return coachClientRepo.end(relId);
}
